import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { Objective1 } from '../../types';
import { useState, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import {
  updateObjective,
  createObjective,
  updateMilestoneMap,
  deleteObjective,
} from '../../store/map3PendingSlice';
import { AppDispatch } from '../../store/store';
import { toggleDrawer } from '../../store/map3UISlice';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { calculateObjectiveRemovalUpdates } from '../../utils/objective-utils';
import { ObjectiveTooltip2 } from './ObjectiveTooltip2';
import { AddEditDeleteBar1 } from './AddEditDeleteBar1';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../../utils/getPermissionsForRole';
import { selectHighlightedAllocations } from '../../store/selectors/highlightSelectors';

interface ObjectiveCell2Props {
  uuid: string;
  isParent: boolean;
  row: number;
  column: number;
  focusPosition: { row: number; col: number };
  hoverPosition: { row: number; col: number };
  milestoneMapUuid: string;
}

export function ObjectiveCell2({
  uuid,
  isParent,
  row,
  column,
  focusPosition,
  hoverPosition,
  milestoneMapUuid,
}: ObjectiveCell2Props) {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mapShortId } = useParams();

  // Derive active objective from ?objectiveId= query param
  const activeObjectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [isInUse, setIsInUse] = useState(false);
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);
  const [startInEditMode, setStartInEditMode] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === uuid),
  );

  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === milestoneMapUuid),
  );

  const allLoadedObjectives = useSelector((state: RootState) => state.map3Data.objectives);

  const isAddExistingObjectivesEnabled = useSelector(
    (state: RootState) => state.map3UI.mapControls.enableAddExistingObjectives,
  );

  // Check user's role for edit/delete permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);
  const canRemove = isAddExistingObjectivesEnabled && canEdit;

  const classNames = [
    'h-full',
    'w-full',
    'flex',
    'items-center',
    'gap-2',
    'px-2',
    'py-1',
    'min-h-8',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-blue-500',
    'cursor-pointer',
  ];

  if (!isParent) {
    classNames.push('pl-8');
  }

  // Human highlight state
  const highlightedAllocations = useSelector(selectHighlightedAllocations);
  const enableHumanHighlight = highlightedAllocations.length > 0;
  const isHighlightedObjective = enableHumanHighlight
    ? highlightedAllocations.some((alloc) => alloc.objectiveUuid === uuid)
    : false;

  // Apply blue ring when this cell's objective is open in the drawer,
  // but NOT when the drawer was opened by a StatusCell3 click (?tab=status).
  // In that case, the ring belongs on the status cell, not the objective cell.
  const activeTabParam = searchParams.get('tab');
  if (
    objective?.shortId &&
    objective.shortId === activeObjectiveShortId &&
    activeTabParam !== 'status'
  ) {
    classNames.push('ring-2', 'ring-blue-500');
  }

  // If crosshairs are enabled, add fancy business for highlighting rows and columns.
  const enableCrosshairs = useSelector(
    (state: RootState) => state.map3UI.mapControls.enableCrosshairs,
  );

  // Read drawer crosshairs state for Status tab highlight
  const highlightedObjectiveUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedObjectiveUuid,
  );
  const highlightedIntervalEnding = useSelector(
    (state: RootState) => state.map3UI.highlightedIntervalEnding,
  );
  const highlightedResultUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedResultUuid,
  );
  const isObjectiveHighlighted = !!highlightedObjectiveUuid && highlightedObjectiveUuid === uuid;

  // Opacity priority: human highlight > drawer crosshairs > result highlight > Map3Control crosshairs
  if (enableHumanHighlight) {
    // Human highlight takes precedence over everything else
    classNames.push(isHighlightedObjective ? 'opacity-100' : 'opacity-10');
  } else if (highlightedIntervalEnding) {
    // Drawer crosshairs: Status tab row highlight effect
    // The objective row is highlighted when it matches the highlighted objective UUID
    classNames.push(isObjectiveHighlighted ? 'opacity-100' : 'opacity-10');
  } else if (highlightedResultUuid) {
    // Result highlight: dim all objective label cells — the highlighted cell itself
    // keeps opacity-100 via StatusCell2's result highlight logic
    classNames.push('opacity-10');
  } else if (enableCrosshairs) {
    // Map3Control crosshairs (only when neither human nor drawer highlight is active)
    const isHighlighted = row === focusPosition.row || row === hoverPosition.row;
    classNames.push(isHighlighted ? 'bg-gray-100' : 'bg-white');
  }

  // Objective highlight: show bg-blue-50 when this objective is highlighted via drawer
  if (isObjectiveHighlighted) {
    classNames.push('bg-blue-50');
  }

  const handleEdit = async (newText: string) => {
    try {
      if (!objective) {
        return;
      }

      // Don't save empty text
      const trimmedText = newText.trim();
      if (!trimmedText) {
        console.warn('Cannot save objective with empty text');
        return;
      }

      // Don't save if text hasn't changed
      if (trimmedText === objective.text) {
        return;
      }

      const result = await dispatch(
        updateObjective({
          uuid: objective.uuid,
          updates: {
            text: trimmedText,
          },
        }),
      );

      // Check if the action was rejected
      if (updateObjective.rejected.match(result)) {
        throw new Error(result.error?.message || 'Failed to update objective');
      }
    } catch (error) {
      console.error('Failed to save objective:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        objectiveUuid: objective?.uuid,
        newText,
      });
    }
  };

  // Open drawer in edit mode via setSearchParams (functional merge preserves other params)
  const handleEditFromActionBar = () => {
    if (objective?.shortId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('drawer', 'objective');
        next.set('objectiveId', objective.shortId);
        next.set('tab', 'info');
        next.set('edit', 'title');
        return next;
      });
    }
  };

  const handleAdd = async () => {
    if (!objective || !milestoneMap) return;

    // Don't create an objective without text
    const text = window.prompt('Enter objective text:');
    if (!text?.trim()) {
      console.warn('Cannot create objective with empty text');
      return;
    }

    const now = new Date().toISOString();
    const newUuid = crypto.randomUUID();

    const newObjective: Objective1 = {
      uuid: newUuid,
      shortId: getShortIdFromUuid(newUuid),
      text: text.trim(),
      parentIds: [objective.uuid],
      childIds: [],
      createdDate: now,
      createdBy: 'user',
      statuses: [],
      results: [],
      comments: [],
      ownerName: '',
      ownerId: '',
    };

    try {
      // First create the new objective
      const createResult = await dispatch(createObjective(newObjective));

      // Check if the action was rejected
      if (createObjective.rejected.match(createResult)) {
        throw new Error(createResult.error?.message || 'Failed to create objective');
      }

      const updatedObjectiveUuids = [...milestoneMap.objectiveUuids, newUuid];

      // Then update the milestone map to include the new objective
      const updateResult = await dispatch(
        updateMilestoneMap({
          uuid: milestoneMapUuid,
          updates: {
            objectiveUuids: updatedObjectiveUuids,
          },
        }),
      );

      // Check if the action was rejected
      if (updateMilestoneMap.rejected.match(updateResult)) {
        throw new Error(updateResult.error?.message || 'Failed to update milestone map');
      }
    } catch (error) {
      console.error('Failed to add objective:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        objective: newObjective,
        uuid: milestoneMapUuid,
      });
    }
  };

  const handleDelete = async () => {
    if (!objective || !milestoneMap) return;

    try {
      // First update the milestone map to remove the objective UUID
      const updatedObjectiveUuids = milestoneMap.objectiveUuids.filter(
        (id) => id !== objective.uuid,
      );

      const updateResult = await dispatch(
        updateMilestoneMap({
          uuid: milestoneMapUuid,
          updates: {
            objectiveUuids: updatedObjectiveUuids,
          },
        }),
      );

      // Check if the milestone map update was rejected
      if (updateMilestoneMap.rejected.match(updateResult)) {
        throw new Error(updateResult.error?.message || 'Failed to update milestone map');
      }

      // Then delete the objective from Firestore
      const deleteResult = await dispatch(deleteObjective(objective.uuid));

      // Check if the delete was rejected
      if (deleteObjective.rejected.match(deleteResult)) {
        throw new Error(deleteResult.error?.message || 'Failed to delete objective');
      }

      console.warn(
        `Note: Objective ${objective.uuid} has been deleted. If it is/was included in any other maps this may cause problems. This is a POC feature. In the future we will query to see if any other milestone_map documents in include this objective before proceeding.`,
      );
    } catch (error) {
      console.error('Failed to delete objective:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        objectiveUuid: objective.uuid,
        milestoneMapUuid,
      });
    }
  };

  const handleRemove = async () => {
    if (!objective || !milestoneMap) return;

    try {
      // Create updates object with base properties
      const updates: any = {
        objectiveUuids: milestoneMap.objectiveUuids.filter((id) => id !== objective.uuid),
      };

      // If this is a parent objective, also update parentObjectiveUuids
      if (isParent) {
        updates.parentObjectiveUuids = milestoneMap.parentObjectiveUuids?.filter(
          (id) => id !== objective.uuid,
        );
      }

      const updateResult = await dispatch(
        updateMilestoneMap({
          uuid: milestoneMapUuid,
          updates,
        }),
      );

      // Check if the milestone map update was rejected
      if (updateMilestoneMap.rejected.match(updateResult)) {
        throw new Error(updateResult.error?.message || 'Failed to update milestone map');
      }

      // Calculate and apply parent/child relationship updates for the removal
      const objectivesOnMap = milestoneMap.objectiveUuids
        .map((uuid) => allLoadedObjectives.find((obj) => obj.uuid === uuid))
        .filter((obj): obj is Objective1 => obj !== undefined);

      const relationshipUpdates = calculateObjectiveRemovalUpdates(
        milestoneMap,
        objective.uuid,
        objectivesOnMap,
      );

      // Apply the calculated updates to each objective that needs changes
      Object.entries(relationshipUpdates).forEach(([objectiveUuid, updates]) => {
        dispatch(updateObjective({ uuid: objectiveUuid, updates }));
      });

      console.log(
        `Note: Objective ${objective.uuid} has been removed from map ${milestoneMapUuid}. The objective still exists in Firestore and may be used in other maps.`,
      );
    } catch (error) {
      console.error('Failed to remove objective from map:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        objectiveUuid: objective.uuid,
        milestoneMapUuid,
      });
    }
  };

  const handleInfo = () => {
    if (objective?.shortId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('drawer', 'objective');
        next.set('objectiveId', objective.shortId);
        next.set('tab', 'info');
        return next;
      });
    } else {
      dispatch(toggleDrawer(true));
    }
  };

  // Double-click opens drawer at info tab via setSearchParams
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (objective?.shortId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('drawer', 'objective');
        next.set('objectiveId', objective.shortId);
        next.set('tab', 'info');
        return next;
      });
    }
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsInUse(false);
    setStartInEditMode(false); // Reset edit mode flag
    // Return focus to the objective cell
    if (cellRef.current) {
      cellRef.current.focus();
    }
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isInUse) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (!isInUse) {
        setIsOpen(true);
      }
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      handleClose();
    }
  };

  return (
    <>
      <div
        ref={cellRef}
        className={classNames.join(' ')}
        data-objective-cell
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsActionBarVisible(true)}
        onMouseLeave={() => setIsActionBarVisible(false)}
        onFocus={() => setIsActionBarVisible(true)}
        onBlur={() => setIsActionBarVisible(false)}
        tabIndex={-1}
      >
        <div className="flex items-center w-full relative">
          <div className="flex-grow overflow-hidden">
            <div className="truncate">{objective?.text}</div>
          </div>
          <div className="absolute top-1/2 right-0 -translate-y-1/2">
            <AddEditDeleteBar1
              isVisible={isActionBarVisible}
              showAdd={false}
              showEdit={canEdit}
              showDelete={canEdit}
              showRemove={canRemove}
              showInfo={true}
              onEdit={handleEditFromActionBar}
              onAdd={handleAdd}
              onDelete={handleDelete}
              onRemove={handleRemove}
              onInfo={handleInfo}
            />
          </div>
        </div>
      </div>
      <ObjectiveTooltip2
        cellId={`objective-${row}-${column}`}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isInUse={isInUse}
        setIsInUse={setIsInUse}
        onClose={handleClose}
        referenceElement={cellRef.current}
        objectiveUuid={uuid}
        milestoneMapUuid={milestoneMapUuid}
        onEdit={handleEdit}
        onAdd={handleAdd}
        onDelete={handleDelete}
        onRemove={handleRemove}
        onInfo={handleInfo}
        showAdd={false}
        showEdit={canEdit}
        showDelete={canEdit}
        showRemove={canRemove}
        startInEditMode={startInEditMode}
      />
    </>
  );
}
